/**
 * The texel passes Godot's texture importer runs after the alpha-border pass
 * (`editor/import/resource_importer_texture.cpp:866-874`), and the channels its compression keeps,
 * on RGBA8 bytes in place. Each returns whether any byte changed, so a caller can keep the
 * original image.
 */
import { byteOf, channelOf } from '../../godot/project/image.js';
/** `Image::premultiply_alpha` (`core/io/image.cpp:4220-4240`), which runs only on an RGBA8 image. */
export function premultiplyAlpha(data) {
    let changed = false;
    for (let i = 0; i < data.length; i += 4) {
        const alpha = data[i + 3];
        for (let c = i; c < i + 3; c++) {
            const premultiplied = (data[c] * alpha + 255) >> 8;
            if (premultiplied === data[c])
                continue;
            data[c] = premultiplied;
            changed = true;
        }
    }
    return changed;
}
/**
 * `_invert_y_channel` (`resource_importer_texture.cpp:657-671`): green becomes `1 - green` through
 * `get_pixel` and `set_pixel`, in float32, truncated to a byte (`core/io/image.cpp:3555-3580`).
 */
export function invertGreen(data) {
    let changed = false;
    for (let i = 0; i < data.length; i += 4)
        changed = writeChannel(data, i + 1, invertedGreen(data, i)) || changed;
    return changed;
}
/**
 * `invertGreen` on a greyscale image, which stores the brightest channel, `Color::get_v`, so every
 * channel takes it.
 */
export function invertGreyscaleGreen(data) {
    let changed = false;
    for (let i = 0; i < data.length; i += 4) {
        const brightest = Math.max(channelOf(data[i]), invertedGreen(data, i), channelOf(data[i + 2]));
        for (let c = i; c < i + 3; c++)
            changed = writeChannel(data, c, brightest) || changed;
    }
    return changed;
}
/** `1 - green` of the texel at `index`, in float32. */
function invertedGreen(data, index) {
    return Math.fround(1 - channelOf(data[index + 1]));
}
/**
 * The swizzle `rg01` of an RGTC or DXT5_RA_AS_RG texture: red and green kept, blue 0 and alpha
 * opaque, since it decodes to R8 or RG8 (`modules/bcdec/image_decompress_bcdec.cpp:245-253, 297-299`).
 */
export function keepRedGreen(data) {
    let changed = false;
    for (let i = 0; i < data.length; i += 4) {
        if (data[i + 2] === 0 && data[i + 3] === 255)
            continue;
        data[i + 2] = 0;
        data[i + 3] = 255;
        changed = true;
    }
    return changed;
}
/** Writes `value` as `set_pixel` does, and returns whether the byte moved. */
function writeChannel(data, index, value) {
    const byte = byteOf(value);
    if (byte === data[index])
        return false;
    data[index] = byte;
    return true;
}
//# sourceMappingURL=importTexelPasses.js.map